import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ApproveJoinGroupRequestsResponse,
  JoinGroupRequestApprovedEnvelope,
  JoinGroupRequestCreatedEnvelope,
  JoinGroupRequestQuery,
  JoinGroupRequestRejectedEnvelope,
  JoinGroupRequestsQueryBuilder,
  ListJoinGroupRequestsOptions,
  ListJoinGroupRequestsResponse,
  QueryJoinGroupRequestsOptions,
  QueryJoinGroupRequestsResponse,
  RejectJoinGroupRequestsResponse,
  Rejection,
  approveJoinGroupRequests as universalApproveJoinGroupRequests,
  listJoinGroupRequests as universalListJoinGroupRequests,
  queryJoinGroupRequests as universalQueryJoinGroupRequests,
  rejectJoinGroupRequests as universalRejectJoinGroupRequests,
  typedQueryJoinGroupRequests as universalTypedQueryJoinGroupRequests,
} from './social-groups-v2-join-group-request-join-group-requests.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/groups' };

export function approveJoinGroupRequests(
  httpClient: HttpClient
): ApproveJoinGroupRequestsSignature {
  return (groupId: string, memberIds: string[]) =>
    universalApproveJoinGroupRequests(
      groupId,
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface ApproveJoinGroupRequestsSignature {
  /**
   * Approves pending join group requests.
   * Group managers always have access to this functionality. In some cases, site owners will allow group members to use this functionality as well.
   * @param - IDs of the site members to approve.
   * @param - ID of the group requested to join.
   */
  (groupId: string, memberIds: string[]): Promise<
    NonNullablePaths<
      ApproveJoinGroupRequestsResponse,
      | `joinGroupRequests`
      | `joinGroupRequests.${number}.siteMemberId`
      | `joinGroupRequests.${number}.contactId`
      | `joinGroupRequests.${number}.status`,
      4
    >
  >;
}

export function rejectJoinGroupRequests(
  httpClient: HttpClient
): RejectJoinGroupRequestsSignature {
  return (groupId: string, rejections: Rejection[]) =>
    universalRejectJoinGroupRequests(
      groupId,
      rejections,
      // @ts-ignore
      { httpClient }
    );
}

interface RejectJoinGroupRequestsSignature {
  /**
   * Rejects pending join group requests.
   * Group managers always have access to this functionality. In some cases, site owners will allow group members to use this functionality as well.
   * @param - Rejection info.
   * @param - ID of the group requested to join.
   */
  (groupId: string, rejections: Rejection[]): Promise<
    NonNullablePaths<
      RejectJoinGroupRequestsResponse,
      | `joinGroupRequests`
      | `joinGroupRequests.${number}.siteMemberId`
      | `joinGroupRequests.${number}.contactId`
      | `joinGroupRequests.${number}.status`,
      4
    >
  >;
}

export function listJoinGroupRequests(
  httpClient: HttpClient
): ListJoinGroupRequestsSignature {
  return (groupId: string, options?: ListJoinGroupRequestsOptions) =>
    universalListJoinGroupRequests(
      groupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListJoinGroupRequestsSignature {
  /**
   * Lists requests to join a group.
   *
   * > **Note:** This function is only relevant for private groups.
   *
   * The `listjoinGroupRequests()` function returns a Promise that resolves to a list of up to 100 requests to join a group. Sorts by default to `_createdDate` in descending order.
   * Only group admins can see requests to join their group. Site members can access their own join requests in their site.
   *
   * > **Notes:**
   * <!-- > + If the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can see requests to join a group. -->
   * @param - ID of the group requested to join.
   */
  (groupId: string, options?: ListJoinGroupRequestsOptions): Promise<
    NonNullablePaths<
      ListJoinGroupRequestsResponse,
      | `joinGroupRequests`
      | `joinGroupRequests.${number}.siteMemberId`
      | `joinGroupRequests.${number}.contactId`
      | `joinGroupRequests.${number}.status`,
      4
    >
  >;
}

export function queryJoinGroupRequests(
  httpClient: HttpClient
): QueryJoinGroupRequestsSignature {
  return (groupId: string, options?: QueryJoinGroupRequestsOptions) =>
    universalQueryJoinGroupRequests(
      groupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryJoinGroupRequestsSignature {
  /**
   * Creates a query to retrieve a list of join requests.
   *
   * > **Notes:**
   * > + This function is only relevant for private groups.
   * > + For `SECRET` groups, only group admins can query requests to join their group. <!-- However, if the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can query requests to join a group. -->
   *
   * The `queryjoinGroupRequests()` function builds a query to retrieve a list of all requests to join a group, and returns a `JoinGroupRequestsQueryBuilder` object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](/join-group-requests-query-builder/find) function.
   *
   * You can refine the query by chaining `joinGroupRequestsQueryBuilder` functions onto the query. `joinGroupRequestsQueryBuilder` functions enable you to sort, filter, and control the results that `queryjoinGroupRequests()` returns.
   *
   * The results of the `queryjoinGroupRequests()` function are sorted by `_createdDate` in descending order.
   *
   * `queryjoinGroupRequests()` runs with this `joinGroupRequestsQueryBuilder` default, which you can override:
   * + [`limit(100)`](/join-group-requests-query-builder/limit)
   *
   * The following `joinGroupRequestsQueryBuilder` functions are supported for `queryjoinGroupRequests()`. For a full description of the joinGroupRequests object, see the object returned for the [`items`](/join-group-requests-query-result/items) property in `JoinGroupRequestsQueryResult`.
   *
   * @param - Group ID.
   */
  (
    groupId: string,
    options?: QueryJoinGroupRequestsOptions
  ): JoinGroupRequestsQueryBuilder;
}

export function typedQueryJoinGroupRequests(
  httpClient: HttpClient
): TypedQueryJoinGroupRequestsSignature {
  return (
    query: JoinGroupRequestQuery,
    options?: QueryJoinGroupRequestsOptions
  ) =>
    universalTypedQueryJoinGroupRequests(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryJoinGroupRequestsSignature {
  /** */
  (
    query: JoinGroupRequestQuery,
    options?: QueryJoinGroupRequestsOptions
  ): Promise<
    NonNullablePaths<
      QueryJoinGroupRequestsResponse,
      | `joinGroupRequests`
      | `joinGroupRequests.${number}.siteMemberId`
      | `joinGroupRequests.${number}.contactId`
      | `joinGroupRequests.${number}.status`,
      4
    >
  >;
}

export const onJoinGroupRequestApproved = EventDefinition(
  'wix.social_groups.v2.join_group_request_approved',
  true,
  (event: JoinGroupRequestApprovedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.joinGroupRequest.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinGroupRequestApprovedEnvelope>();
export const onJoinGroupRequestCreated = EventDefinition(
  'wix.social_groups.v2.join_group_request_created',
  true,
  (event: JoinGroupRequestCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinGroupRequestCreatedEnvelope>();
export const onJoinGroupRequestRejected = EventDefinition(
  'wix.social_groups.v2.join_group_request_rejected',
  true,
  (event: JoinGroupRequestRejectedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.joinGroupRequest.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinGroupRequestRejectedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApproveAllJoinGroupRequestsRequest,
  ApproveAllJoinGroupRequestsResponse,
  ApproveJoinGroupRequestsRequest,
  ApproveJoinGroupRequestsResponse,
  BaseEventMetadata,
  CancelJoinGroupRequestRequest,
  CancelJoinGroupRequestResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  EventsViolationOptions,
  GetJoinRequirementsRequest,
  GetJoinRequirementsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemsToUpdate,
  JoinGroupRequest,
  JoinGroupRequestApproved,
  JoinGroupRequestApprovedEnvelope,
  JoinGroupRequestCancelled,
  JoinGroupRequestCreatedEnvelope,
  JoinGroupRequestQuerySpec,
  JoinGroupRequestRejected,
  JoinGroupRequestRejectedEnvelope,
  JoinGroupRequestsQueryBuilder,
  JoinGroupRequestsQueryResult,
  JoinRequestsApproved,
  ListAllJoinGroupRequestsRequest,
  ListAllJoinGroupRequestsResponse,
  ListJoinGroupRequestsOptions,
  ListJoinGroupRequestsRequest,
  ListJoinGroupRequestsResponse,
  MemberJoinedGroup,
  MembersAddedToGroup,
  MembersInvitedToGroup,
  MembershipQuestionAnswer,
  MembershipQuestionViolationOptions,
  MessageEnvelope,
  OwnershipFilter,
  Paging,
  PagingMetadata,
  PaidPlan,
  PricingPlanViolationOptions,
  Query,
  QueryJoinGroupRequestsOptions,
  QueryJoinGroupRequestsRequest,
  QueryJoinGroupRequestsResponse,
  RejectAllJoinGroupRequestsRequest,
  RejectAllJoinGroupRequestsResponse,
  RejectJoinGroupRequestsRequest,
  RejectJoinGroupRequestsResponse,
  Rejection,
  RequestDetails,
  RequestStatus,
  RestoreInfo,
  SocialGroupsEvent,
  SocialGroupsEventPayloadOneOf,
  SortOrder,
  Sorting,
  SubmitJoinGroupRequestRequest,
  SubmitJoinGroupRequestResponse,
  Violation,
  ViolationType,
  ViolationViolationOptionsOneOf,
  WebhookIdentityType,
  utils,
} from './social-groups-v2-join-group-request-join-group-requests.universal.js';
